import { useQuery } from '@apollo/client';
import { Button, Col, Input, Pagination, Row } from 'antd';
import { CaretLeftOutlined, CaretRightOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import React, { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { noop } from 'lodash-es';

import NewShipmentDrawer from '../../drawers/NewShipmentDrawer';
import { useUserTemplateManagementStyles } from './UserTemplateManagement.styles';
import { SelectAccountDropdown } from '../../../containers/UserAddressBook';
import { FAIcon } from '../../adapters/fontAwesomeAdapters';
import { FlexCol, TwoLines } from '../../layout/layoutElements';
import { useLayoutElementsStyles } from '../../layout/layoutElements.styles';
import IconButton from '../../buttons/IconButton';
import { useShipmentTemplatesActiveItem } from '../../../forms/shipments/shipmentTemplates/shipmentTemplateHooks';
import { ShipmentTemplateDrawer } from '../../../forms/shipments/shipmentTemplates/ShipmentTemplateDrawer';
import useNewShipmentFormState from '../../../forms/shipments/newShipment/useNewShipmentFormState';
import { JOB_TEMPLATES_QUERY } from '../../../app/graphql/jobTemplateQueries';
import { extractGraphqlEntity } from '../../../common/utils/graphqlUtils';
import { renderDataStateIndicator } from '../../data/dataStateHandlers';
import { DynamicFormDependenciesProvider } from '../../forms/dynamic/dynamicFormDependencies';
import { useDynamicTablePageSize } from '../../../dialogs/invoice/invoiceUtils/useDynamicTablePageSize';

export default function UserTemplateManagement({ open, onClose }) {
  const { formatMessage } = useIntl();
  const [filter, setFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const { styles } = useUserTemplateManagementStyles();
  const { styles: layoutStyles } = useLayoutElementsStyles();

  const { containerRef, pageSize } = useDynamicTablePageSize({
    rowHeight: 73,
    fixedTop: 0,
    fixedBottom: 50,
    buffer: 8,
  });

  const { forms, account, setAccount, values } = useNewShipmentFormState();

  const { activeItem, afterSave, afterDelete } = useShipmentTemplatesActiveItem(
    {
      forms,
      afterApply: noop,
      drawerData: {},
    }
  );

  const { data, loading, error } = useQuery(JOB_TEMPLATES_QUERY, {
    variables: { accountNumber: account?.number },
    notifyOnNetworkStatusChange: true,
    skip: !account?.number,
  });

  const indicator = renderDataStateIndicator({ data, loading, error });
  const items = useMemo(
    () => (data ? extractGraphqlEntity(data).data : []),
    [data]
  );

  const filteredItems = useMemo(() => {
    const filterQuery = filter.trim().toLowerCase();
    if (filterQuery) {
      return items.filter(item =>
        item?.name?.toLowerCase()?.includes(filterQuery)
      );
    }
    return items;
  }, [filter, items]);

  const paginatedItems = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredItems.slice(startIndex, startIndex + pageSize);
  }, [filteredItems, currentPage, pageSize]);

  const handleFilterChange = event => {
    setFilter(event.target.value);
    setCurrentPage(1);
  };

  const readOnly = !!account?.partner;
  const [drawerData, setDrawerData] = useState({});
  const closeDrawer = () => setDrawerData({});

  const handleEdit = item => {
    setDrawerData({ editing: item });
  };

  const handleNew = () => {
    setDrawerData({ adding: true });
  };

  const isEditingOrAdding = drawerData.editing || drawerData.adding;

  const close = () => {
    onClose();
    closeDrawer();
    setAccount(null);
    setFilter('');
    setCurrentPage(1);
  };

  const renderDrawerContent = () => {
    if (isEditingOrAdding) {
      return (
        <DynamicFormDependenciesProvider
          values={{ ...values, accountNumber: account?.number }}
        >
          <ShipmentTemplateDrawer
            onClose={closeDrawer}
            data={drawerData}
            setData={setDrawerData}
            afterSave={entry => {
              afterSave(entry);
            }}
            afterDelete={entry => {
              afterDelete(entry);
              closeDrawer();
            }}
            values={values}
            activeItem={activeItem}
            readOnly={readOnly}
            renderContentOnly
          />
        </DynamicFormDependenciesProvider>
      );
    }

    if (account) {
      return (
        <FlexCol className={layoutStyles.flex1}>
          <Row gutter={10} className={styles.templateManagementHeader}>
            <Col flex="auto">
              <Input
                value={filter}
                placeholder={formatMessage({
                  id: 'shipmentTemplates.placeholder',
                })}
                className="InputSearchFlat"
                prefix={<IconButton icon="search" size="sm" />}
                onChange={handleFilterChange}
              />
            </Col>
            <Col>
              <Button
                className="width-70"
                type="primary"
                ghost
                onClick={handleNew}
              >
                <span>
                  <FormattedMessage id="buttons.new" />
                </span>
                <FAIcon icon="edit" />
              </Button>
            </Col>
          </Row>
          <TwoLines className="size-6" />
          <div
            className={classNames(
              styles.templateListContainer,
              layoutStyles.flex1
            )}
            ref={containerRef}
          >
            {indicator || (
              <>
                <div
                  className={styles.templateList}
                  data-testid="template-list"
                >
                  {paginatedItems.map(item => (
                    <div key={item.id} className={styles.templateListItem}>
                      <div className={styles.templateName}>
                        <span>
                          <FormattedMessage id="shipmentTemplates.name" />
                        </span>
                        {item.name}
                      </div>
                      <div
                        className={styles.editButton}
                        onClick={() => handleEdit(item)}
                      >
                        <FAIcon icon="edit" />
                      </div>
                    </div>
                  ))}
                </div>
                {filteredItems.length > pageSize && (
                  <div
                    className={styles.templateListPagination}
                    style={{
                      '--page-label': `"${formatMessage({ id: 'dynamicTable.page' })}"`,
                      '--out-of-label': `"${formatMessage(
                        { id: 'dynamicTable.outOf' },
                        {
                          totalPages: Math.ceil(
                            filteredItems.length / pageSize
                          ),
                        }
                      )}"`,
                    }}
                  >
                    <Pagination
                      simple
                      current={currentPage}
                      pageSize={pageSize}
                      total={filteredItems.length}
                      onChange={setCurrentPage}
                      itemRender={(_, type, originalElement) => {
                        if (type === 'prev') {
                          return <CaretLeftOutlined />;
                        }
                        if (type === 'next') {
                          return <CaretRightOutlined />;
                        }
                        return originalElement;
                      }}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </FlexCol>
      );
    }

    return (
      <SelectAccountDropdown
        setSelectedAccount={setAccount}
        subTitleId="account.templatesManagement.subtitle"
      />
    );
  };

  return (
    <NewShipmentDrawer
      className={styles.drawer}
      header={<FormattedMessage id="shipmentTemplates.drawerTitle" />}
      open={open}
      onClose={close}
    >
      {renderDrawerContent()}
    </NewShipmentDrawer>
  );
}
